import { Injectable, Type } from '@angular/core';
import { IDecoratedComponent } from './decorated-component.interface';
import { DecoratorConfig } from './decorator-config';
import { DecoratorFactory } from './decorator-factory';
import { IShortLogger, Log } from '../services/logger.service';

/**
 * Optimized universal decoration service that handles all components regardless of their decorator types.
 * Uses DecoratorFactory's static data structure as the single source of truth for all registrations.
 */
@Injectable({
  providedIn: 'root'
})
export class DecoratorService {
  private readonly logger: IShortLogger<DecoratorService> = Log.create(DecoratorService);

  constructor() {
  }

  /**
   * Access the static data structure from DecoratorFactory
   * This ensures a single source of truth for all registrations
   */
  private get componentsByDecorator(): Map<string, Map<Type<IDecoratedComponent>, string>> {
    return (DecoratorFactory as any).componentsByDecorator;
  }

  /**
   * Lazy-loaded projection of all components from componentsByDecorator
   * Returns a Map of componentName -> componentType for O(1) lookups
   */
  get components(): Map<string, Type<IDecoratedComponent>> {
    const registry = new Map<string, Type<IDecoratedComponent>>();

    for (const decoratorMap of this.componentsByDecorator.values()) {
      for (const [componentType, componentName] of decoratorMap.entries()) {
        registry.set(componentName, componentType);
      }
    }

    return registry;
  }

  private normalizeKey(key: string): string {
    return key.toLowerCase().trim();
  }

  /**
   * Register a component with a specific decorator
   * This is the single registration method that adds to the main structure
   *
   * @param decoratorName - The decorator name (normalized)
   * @param componentType - The component type to register
   */
  registerComponentWithDecorator(decoratorName: string, componentType: Type<IDecoratedComponent>): void {
    const normalizedName = this.normalizeKey(decoratorName);

    this.logger.debug( `Attempting to register ${componentType.name} with decorator ${normalizedName}`);
    this.logger.debug( `Current componentsByDecorator state:`, this.componentsByDecorator);

    // Initialize decorator map if it doesn't exist
    if (!this.componentsByDecorator.has(normalizedName)) {
      this.componentsByDecorator.set(normalizedName, new Map());
      this.logger.debug( `Created new decorator map for ${normalizedName}`);
    }

    const decoratorMap = this.componentsByDecorator.get(normalizedName)!;

    if (decoratorMap.has(componentType)) {
      this.logger.debug( `Component ${componentType.name} already registered with ${normalizedName}, skipping duplicate`);
      return;
    }

    decoratorMap.set(componentType, componentType.name);
    this.logger.debug( `Successfully registered component: ${componentType.name} with decorator: ${normalizedName}`);
    this.logger.debug( `Updated componentsByDecorator state:`, this.componentsByDecorator);
  }

  /**
   * Get all registered components
   *
   * @returns Set of all registered component types
   */
  getAllComponents(): Set<Type<IDecoratedComponent>> {
    const components = new Set<Type<IDecoratedComponent>>();

    for (const decoratorMap of this.componentsByDecorator.values()) {
      for (const componentType of decoratorMap.keys()) {
        components.add(componentType);
      }
    }

    return components;
  }

  /**
   * Get all registered component names
   *
   * @returns Array of component names
   */
  getAllComponentNames(): string[] {
    return Array.from(this.components.keys());
  }

  /**
   * Get all components decorated with a specific decorator
   * Now O(1) lookup using the decorator mapping!
   *
   * @param decoratorName - The decorator name to search for
   * @returns Array of component types that have the specified decoration
   */
  getDecoratedComponents(decoratorName: string): Type<IDecoratedComponent>[] {
    const normalizedName = this.normalizeKey(decoratorName);
    const decoratorMap = this.componentsByDecorator.get(normalizedName);

    if (!decoratorMap) {
      throw new Error(`No decorator registered/created by name: ${decoratorName}(normalized as: ${normalizedName}).
        Available decorators: ${this.getAllDecoratorNames().join(", ")}`, {
        cause: `KeyNotFound`
      })
    }

    return Array.from(decoratorMap.keys());
  }

  /**
   * Get components that have a specific configuration type
   * Now uses the optimized decorator mapping for better performance
   *
   * @param configKey - The configuration key to search for (e.g., 'cloudresource', SIDE_BAR)
   * @returns Array of component types that have the specified configuration
   */
  getComponentsWithConfig(configKey: string): Type<IDecoratedComponent>[] {
    // Use the decorator mapping (much faster than creating instances)
    return this.getDecoratedComponents(configKey);
  }

  /**
   * Get a specific configuration from a component
   *
   * @param componentType - The component type
   * @param configKey - The configuration key
   * @returns The configuration object or undefined
   */
  getComponentConfig<T extends DecoratorConfig>(
    componentType: Type<IDecoratedComponent>,
    configKey: string
  ): T | undefined {
    const tempInstance = new componentType();
    return tempInstance.getConfigByKey?.<T>(configKey);
  }

  /**
   * Get components by class name - O(1) with Map!
   *
   * @param componentName - The component class name
   * @returns The component type or undefined
   */
  getComponentByName(componentName: string): Type<IDecoratedComponent> | undefined {
    return this.components.get(componentName);
  }

  /**
   * Get all configuration keys used across all registered components
   *
   * @returns Set of unique configuration keys
   */
  getAllConfigKeys(): Set<string> {
    return new Set(this.componentsByDecorator.keys());
  }

  /**
   * Find components with specific configuration values
   *
   * @param configKey - The configuration key to search in
   * @param predicate - Function to test configuration values
   * @returns Array of matching component types
   */
  findComponentsWhere<T extends DecoratorConfig>(
    configKey: string,
    predicate: (config: T) => boolean
  ): Type<IDecoratedComponent>[] {
    const result: Type<IDecoratedComponent>[] = [];
    const components = this.getDecoratedComponents(configKey);

    for (const componentType of components) {
      const config = this.getComponentConfig<T>(componentType, configKey);
      if (config && predicate(config)) {
        result.push(componentType);
      }
    }

    return result;
  }

  /**
   * Get component counts by configuration type
   *
   * @returns Object with configuration keys and their counts
   */
  getComponentCountsByConfig(): Record<string, number> {
    const counts: Record<string, number> = {};

    this.componentsByDecorator.forEach((componentMap, decoratorName) => {
      counts[decoratorName] = componentMap.size;
    });

    return counts;
  }

  /**
   * Get all decorator names that have components
   *
   * @returns Array of decorator names that have been used
   */
  getAllDecoratorNames(): string[] {
    return Array.from(this.componentsByDecorator.keys());
  }

  /**
   * Get component counts by decorator type
   * This provides a summary of how many components use each decorator
   *
   * @returns Object with decorator names as keys and counts as values
   */
  getComponentCountsByDecorator(): Record<string, number> {
    const counts: Record<string, number> = {};

    this.componentsByDecorator.forEach((components, decoratorName) => {
      counts[decoratorName] = components.size;
    });

    return counts;
  }

  /**
   * Check if a decorator has any components
   *
   * @param decoratorName - The decorator name to check
   * @returns True if the decorator has components, false otherwise
   */
  hasDecoratedComponents(decoratorName: string): boolean {
    const components = this.componentsByDecorator.get(decoratorName.toLowerCase());
    return components ? components.size > 0 : false;
  }

  /**
   * Check if a component is registered
   *
   * @param componentName - The component name to check
   * @returns True if the component is registered, false otherwise
   */
  isComponentRegistered(componentName: string): boolean {
    return this.components.has(componentName);
  }

  /**
   * Get the total number of registered components
   *
   * @returns Number of registered components
   */
  getComponentCount(): number {
    return this.getAllComponents().size;
  }
}
